// Angular
import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
// PrimeNG
import { DividerModule } from 'primeng/divider';
// Models
import { RecipientType } from './models/recipient-type.type';
import { IGuest } from '@core/models-v2/storage/guest.interface';
import { ISession } from '@core/models-v2/auth/session.interface';
import {
  IShoppingCart,
  IShoppingCartGroup,
} from '@core/models-v2/cart/shopping-cart.interface';
import { RecipientAnother } from './components/recipient-another-form/models/recipient-another.interface';
// Services
import { CheckoutService } from '../../services/checkout/checkout.service';
import { GuestStorageService } from '@core/storage/guest-storage.service';
import { SessionService } from '@core/services-v2/session/session.service';
// Components
import { ShippingTypeFormComponent } from './components/shipping-type-form/shipping-type-form.component';
import { RecipientTypeFormComponent } from './components/recipient-type-form/recipient-type-form.component';
import { RecipientAnotherFormComponent } from './components/recipient-another-form/recipient-another-form.component';
import { PickupStoreFormComponent } from './components/pickup-store-form/pickup-store-form.component';
import { DeliveryAddressFormComponent } from './components/delivery-address-form/delivery-address-form.component';
import { ShippingProductListComponent } from './components/shipping-product-list/shipping-product-list.component';

import { SessionUtils } from 'src/app/modules/cart/page/page-cart-shipping/utils/session-utils.service';
import { CartApiService } from '@core/services-v2/cart/cart-api/cart-api.service';
import { CartStorageService } from '@core/services-v2/cart/cart-storage/cart-storage.service';
import { CheckoutStorageService } from '../../services/checkout-storage/checkout-storage.service';
import { AddressOption } from './components/delivery-address-form/models/address-option.type';
import { Store } from '@core/services-v2/geolocation/models/store.interface';
import { GtmService } from '@core/utils-v2/gtm/gtm.service';

declare let dataLayer: any;

const PRIME_MODULES = [DividerModule];
const COMPONENTS = [
  ShippingTypeFormComponent,
  RecipientTypeFormComponent,
  RecipientAnotherFormComponent,
  PickupStoreFormComponent,
  DeliveryAddressFormComponent,
  ShippingProductListComponent,
];

@Component({
  selector: 'app-shipping-step',
  standalone: true,
  imports: [PRIME_MODULES, COMPONENTS],
  templateUrl: './shipping-step.component.html',
  styleUrl: './shipping-step.component.scss',
  host: { class: 'w-full mb-6 lg:mb-0' },
})
export class ShippingStepComponent implements OnInit {
  //#region ViewChild Components
  @ViewChild('recipientAnotherFormComponent')
  recipientAnotherForm!: RecipientAnotherFormComponent;
  @ViewChild('deliveryAddressFormComponent')
  deliveryAddressFormComponent!: DeliveryAddressFormComponent;
  @ViewChild('shippingProductListcomponent')
  shippingProductListcomponent!: ShippingProductListComponent;
  @ViewChild('pickupStoreFormComponent')
  pickupStoreFormComponent!: PickupStoreFormComponent;
  //#endregion

  recipientType = signal<RecipientType>('SELF');
  shippingType = signal<'Retiro' | 'Despacho'>('Retiro');
  addressShipping = signal<any>(null);

  cartSession!: IShoppingCart;
  userSession!: ISession;
  guest!: IGuest;
  recipientAnother: RecipientAnother | null = null;
  hasShippingGroups = signal<boolean>(false);

  //#region Dependencies
  public readonly checkoutService = inject(CheckoutService);
  private readonly cartApiService = inject(CartApiService);
  private readonly cartStorageService = inject(CartStorageService);
  private readonly guestStorage = inject(GuestStorageService);
  private readonly sessionService = inject(SessionService);
  private readonly checkoutStorageService = inject(CheckoutStorageService);
  private readonly gtmService = inject(GtmService);
  //#endregion

  //#region Lifecycle Hooks
  ngOnInit(): void {
    this.userSession = this.sessionService.getSession();
    this.cartSession = this.cartStorageService.get()!;
    if (this.cartSession.shipment?.deliveryMode === 'pickup')
      this.shippingType.set('Retiro');
    else this.shippingType.set('Despacho');
    this.recipientAnother = this.checkoutStorageService.getRecipientAnother();
    this.recipientType.set(this.recipientAnother ? 'ANOTHER' : 'SELF');
    this.setNotificationContact();
  }
  //#endregion

  onShippingGroupsChange(hasShippingGroups: boolean): void {
    this.hasShippingGroups.set(hasShippingGroups);
  }

  /**
   * Actualiza el contacto de notificación del carro de compras.
   */
  private setNotificationContact(): void {
    this.guest = this.guestStorage.get() as IGuest;
    const contact = SessionUtils.getContact(this.userSession, this.guest);
    if (!contact?.name) return;
    this.cartApiService
      .updateNotificationContact(this.cartSession._id!.toString(), contact)
      .subscribe();
  }

  /**
   * Cambia el tipo de destinatario.
   * @param type - Tipo de destinatario.
   */
  onRecipientTypeChange(type: RecipientType): void {
    this.recipientType.set(type);
  }

  onSelectDeliveryAddress(address: AddressOption | null): void {
    this.addressShipping.set(address);
    this.checkoutStorageService.setDeliveryAddress(address as AddressOption);
  }

  onSelectPickupStore(store: Store): void {
    this.addressShipping.set(store);
    this.checkoutStorageService.setPickupStore(store);
  }

  /**
   * - Este método se invoca desde el componente de dirección de entrega.
   * @returns
   */
  submit(): boolean {
    let isValidDeliveryAddressForm = true;
    let isValidPickupAddressForm = true;
    let isValidshippingProductList =
      this.shippingProductListcomponent.submit();
    if (this.checkoutService.shippingType() === 'DELIVERY') {
      isValidDeliveryAddressForm = this.deliveryAddressFormComponent.submit();
    } else if (this.checkoutService.shippingType() === 'PICKUP') {
      isValidPickupAddressForm = this.pickupStoreFormComponent.submit();
    }

    if (this.recipientType() === 'ANOTHER') {
      const isValidRecipientAnotherForm = this.recipientAnotherForm.submit();
      return isValidRecipientAnotherForm && isValidDeliveryAddressForm;
    }

    let isValidateCutOffTime = this.isValidateCutOffTime();
    if (!isValidateCutOffTime) {
      this.shippingProductListcomponent.reloadPromise();
      return isValidateCutOffTime;
    }
    //this.receiveStorageService.remove();
    const isValid =
      isValidDeliveryAddressForm &&
      isValidshippingProductList &&
      isValidPickupAddressForm;

    if (isValid) {
      this.gtmService.addShippingInfo(dataLayer, this.cartSession);
    }
    return isValid;
  }

  /**
   * Valida si el carro de compras tiene un tiempo de corte válido.
   * @returns boolean - Indica si el carro de compras tiene un tiempo de corte válido.
   */
  private isValidateCutOffTime(): boolean {
    let cartSession = this.cartStorageService.get()!;
    let isValidate = true;
    if (cartSession.groups?.length) {
      let updateDateCart = new Date(cartSession.updatedAt ?? '');
      let nowDate = new Date();
      cartSession.groups?.forEach((group: IShoppingCartGroup) => {
        let cutOffTime = new Date(group.shipment?.cutOffDateTime ?? '');
        if (updateDateCart < cutOffTime && nowDate >= cutOffTime) {
          isValidate = false;
        }
      });
      return isValidate;
    } else {
      let updateDateCart = new Date(cartSession.updatedAt ?? '');
      let nowDate = new Date();
      let cutOffTime = new Date(cartSession.shipment?.cutOffDateTime ?? '');
      if (updateDateCart < cutOffTime && nowDate >= cutOffTime) {
        return false;
      } else {
        return isValidate;
      }
    }
  }
}
